import { useEffect } from "react";
import { Button } from "@/components/ui/button";
import { IntakeData } from "../../pages/Index";
import { CheckCircle } from "lucide-react";

interface ComprehensiveDentalAnalysisProps {
  data: IntakeData;
  onUpdate: (data: Partial<IntakeData>) => void;
  onNext: () => void;
}

export default function ComprehensiveDentalAnalysis({
  data,
  onUpdate: _onUpdate,
  onNext,
}: ComprehensiveDentalAnalysisProps) {
  // Auto-proceed when component mounts
  useEffect(() => {
    if (Object.values(data.photos).filter(Boolean).length >= 9) {
      proceedToNext();
    }
  }, []);

  const proceedToNext = () => {
    // Skip analysis and proceed directly to next step
    console.log("✅ Skipping AI analysis, proceeding to next step");
    onNext();
  };

  return (
    <div className="min-h-screen py-6 px-4 sm:px-6 lg:px-8">
      <div className="max-w-4xl mx-auto space-y-8">
        {/* Header */}
        <div className="text-center space-y-4">
          <div className="w-16 h-16 border-2 border-[#166B93] rounded-2xl flex items-center justify-center mx-auto shadow-lg">
            <CheckCircle className="w-8 h-8 text-[#166B93]" />
          </div>
          <div className="space-y-2">
            <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">
              Photos Received Successfully
            </h1>
            <p className="text-lg text-gray-600">
              Your dental photos have been submitted and will be reviewed by our
              specialists
            </p>
          </div>
        </div>

        {/* Success Message */}
        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-8">
          <div className="text-center space-y-6">
            <div className="w-20 h-20 bg-[#CEF12F]/10 rounded-full flex items-center justify-center mx-auto">
              <CheckCircle className="w-10 h-10 text-[#CEF12F]" />
            </div>
            <div className="space-y-2">
              <h2 className="text-xl font-semibold text-gray-900">
                Thank You!
              </h2>
              <p className="text-gray-600">
                Your photos and information have been successfully submitted.
                Our dental specialists will review your case and provide you
                with a personalized treatment plan.
              </p>
            </div>
            <div className="bg-blue-50 rounded-lg p-4">
              <p className="text-sm text-blue-800">
                <strong>Next Steps:</strong> You will receive an email with your
                personalized treatment plan and next steps within 24-48 hours.
              </p>
            </div>
          </div>
        </div>

        {/* Continue Button */}
        <div className="flex justify-center">
          <Button
            onClick={onNext}
            className="bg-[#166B93] hover:bg-[#166B93]/90 text-white py-3 px-8 text-lg font-semibold"
          >
            Continue to Next Step
          </Button>
        </div>
      </div>
    </div>
  );
}
